import { createRouter, RouterProvider } from "@tanstack/react-router";
import { type ComponentProps, StrictMode } from "react";
import ReactDOM from "react-dom/client";
import "./globals.css";

import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import { DefaultCatchBoundary } from "./components/core/default-catch-boundary";
import { NotFound } from "./components/not-found";
// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { useAuth, useClerk, useSession } from "@clerk/react";
import { ThemeProvider } from "@mui/material";
import { components } from "./styles/theme/components/components";
import { AppProviders } from "./components/core/app-providers";
import { UseAuthReturn } from "@clerk/react/types";
import { PendingComponent } from "./components/core/pending";
import { createAppTheme } from "./styles/theme/create-app-theme";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
});

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: DefaultCatchBoundary,
  defaultPendingComponent: PendingComponent,

  scrollRestoration: true,
  context: {
    auth: {
      isLoaded: false,
    } as unknown as UseAuthReturn,
    session: null,
    clerk: null as unknown as ReturnType<typeof useClerk>,
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2025-05-24" as Exclude<
    ComponentProps<typeof PostHogProvider>["options"],
    undefined
  >["defaults"],
};

// Under the Playwright crawler, every MUI transition is time spent waiting on
// something its aria snapshots cannot see — the ripple on each clicked button
// alone was 550ms. Zeroing the durations, rather than disabling CSS transitions,
// also zeroes the timers MUI unmounts a closing dialog on. `navigator.webdriver`
// keeps `pnpm dev:mock` animated for a person clicking around.
const instant = import.meta.env.VITE_MOCK_API && navigator.webdriver;

const App = () => {
  const theme = createAppTheme({
    ...(instant && {
      components: { ...components, MuiButtonBase: { defaultProps: { disableRipple: true } } },
      transitions: {
        duration: {
          shortest: 0,
          shorter: 0,
          short: 0,
          standard: 0,
          complex: 0,
          enteringScreen: 0,
          leavingScreen: 0,
        },
        // Menus and popovers size their Grow from the element's height instead.
        getAutoHeightDuration: () => 0,
      },
    }),
  });

  return (
    <StrictMode>
      <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
        <ThemeProvider theme={theme}>
          <AppProviders>
            <Router />
          </AppProviders>
        </ThemeProvider>
      </PostHogProvider>
    </StrictMode>
  );
};

const Router = () => {
  const auth = useAuth();
  const { session } = useSession();
  const clerk = useClerk();
  if (auth.isLoaded) {
    return <RouterProvider router={router} context={{ auth, session, clerk }} />;
  }
};

// Serve the whole API from generated mocks instead of the network. Set only by
// the Playwright crawler and by `pnpm dev:mock`. `import.meta.env` is inlined at
// build time, so production builds drop this branch and never emit the chunk.
const startMocks = async () => {
  if (!import.meta.env.VITE_MOCK_API) return;
  const { worker } = await import("./test/browser-mocks");
  await worker.start({ onUnhandledRequest: "warn", quiet: true });
};

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  startMocks().then(() => root.render(<App />));
}

window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});
